import { useEffect, useState } from "react";
import { useParams, useLocation, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api, { getPublicFileUrl, formatApiErrorDetail } from "@/lib/api";
import { PublicLangProvider, usePublicLang } from "@/contexts/PublicLangContext";
import PublicStepper from "@/components/publicBooking/PublicStepper";
import StepServices from "@/components/publicBooking/StepServices";
import StepProfessionals from "@/components/publicBooking/StepProfessionals";
import StepDateTime from "@/components/publicBooking/StepDateTime";
import StepPatientInfo from "@/components/publicBooking/StepPatientInfo";
import StepConfirmation from "@/components/publicBooking/StepConfirmation";
import PublicFooter from "@/components/publicBooking/PublicFooter";
import PublicWhatsAppButton from "@/components/publicBooking/PublicWhatsAppButton";
import { PUBLIC_BOOKING } from "@/constants/testIds";

function PublicBookingContent({ clinic, preselectedServiceId, linkCode, prefill }) {
  const { lang, setLang, t } = usePublicLang();
  const [step, setStep] = useState(1);
  const [service, setService] = useState(null);
  const [professional, setProfessional] = useState(null);
  const [dateTime, setDateTime] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  // Si la persona vino desde la landing eligiendo un tratamiento puntual,
  // saltamos directo a "Elegi un profesional" apenas encontramos ese servicio.
  useEffect(() => {
    if (!preselectedServiceId) return;
    let active = true;
    api.get(`/public/clinics/${clinic.slug}/services`).then(({ data }) => {
      if (!active) return;
      const match = data.find((s) => s.id === preselectedServiceId);
      if (match) { setService(match); setStep(2); }
    });
    return () => { active = false; };
  }, [preselectedServiceId, clinic.slug]);


  const brandStyle = {
    // Fix A (2026-09-04): mismos defaults de marca que la landing.
    // Fix E (2026-09-09): sumamos accent/dark/champagne para poder aplicar
    // la misma identidad visual de la landing (Tiffany/espresso/champagne)
    // en la reserva publica, sin tocar el flujo ni la logica.
    "--brand-primary": clinic.brand_primary_color || "#B99A62",
    "--brand-secondary": clinic.brand_secondary_color || "#F7F3ED",
    "--brand-accent": "#81D8D0",
    "--brand-dark": "#292522",
    "--brand-champagne": "#D8C3A5",
  };

  const goBack = () => setStep((s) => Math.max(s - 1, 1));

  const resetFlow = () => {
    setStep(1);
    setService(null);
    setProfessional(null);
    setDateTime(null);
    setConfirmation(null);
  };

  return (
    <div className="min-h-screen bg-[#F7F3ED] font-sans text-[#292522] flex flex-col" style={brandStyle} data-testid={PUBLIC_BOOKING.page}>
      <header className="sticky top-0 z-50 bg-[#F7F3ED]/90 backdrop-blur-xl border-b border-black/5 py-4 px-4 sm:px-6 flex justify-between items-center">
        <div className="flex items-center gap-2 min-w-0">
          {clinic.logo_url ? (
            <img src={getPublicFileUrl(clinic.logo_url)} alt={clinic.name} className="w-9 h-9 rounded-full object-cover shrink-0" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-[var(--brand-primary)] text-white flex items-center justify-center font-serif text-sm shrink-0">
              {clinic.name?.[0] || "S"}
            </div>
          )}
          <span className="font-serif text-base sm:text-lg uppercase tracking-[0.2em] text-[var(--brand-primary)] truncate">{clinic.name}</span>
        </div>
        <div className="flex rounded-full border border-black/10 overflow-hidden text-xs font-semibold shrink-0" data-testid={PUBLIC_BOOKING.langToggle}>
          <button
            type="button"
            className={`px-3 py-1.5 transition-colors ${lang === "es" ? "bg-[var(--brand-primary)] text-white" : "text-[#5C554D]"}`}
            onClick={() => setLang("es")}
            data-testid={PUBLIC_BOOKING.langEs}
          >
            ES
          </button>
          <button
            type="button"
            className={`px-3 py-1.5 transition-colors ${lang === "en" ? "bg-[var(--brand-primary)] text-white" : "text-[#5C554D]"}`}
            onClick={() => setLang("en")}
            data-testid={PUBLIC_BOOKING.langEn}
          >
            EN
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto w-full px-4 sm:px-6 md:px-8 pt-8 sm:pt-12 pb-28 flex-1">
        {step < 5 && <PublicStepper step={step} />}

        <div key={step} className="animate-in fade-in slide-in-from-bottom-2 duration-500">
        {step === 1 && <StepServices clinic={clinic} onSelect={(s) => { setService(s); setStep(2); }} />}

        {step === 2 && (
          <StepProfessionals
            clinic={clinic}
            service={service}
            onSelect={(p) => { setProfessional(p); setStep(3); }}
            onBack={goBack}
          />
        )}

        {step === 3 && (
          <StepDateTime
            clinic={clinic}
            service={service}
            professional={professional}
            onSelect={(dt) => { setDateTime(dt); setStep(4); }}
            onBack={goBack}
          />
        )}

        {step === 4 && (
          <StepPatientInfo
            clinic={clinic}
            service={service}
            professional={professional}
            dateTime={dateTime}
            onBack={goBack}
            linkCode={linkCode}
            prefill={prefill}
            onConfirmed={(data) => { setConfirmation(data); setStep(5); }}
          />
        )}

        {step === 5 && confirmation && (
          <StepConfirmation confirmation={confirmation} clinic={clinic} onNewBooking={resetFlow} />
        )}
        </div>
      </main>

      <PublicFooter clinic={clinic} />
      <PublicWhatsAppButton clinic={clinic} />
    </div>
  );
}

export default function PublicBookingPage() {
  const { slug } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [clinic, setClinic] = useState(null);
  const [error, setError] = useState(null);
  // Fix AM (2026-10-23): link que mando el agente de WhatsApp (?r=codigo): tratamiento y datos del chat ya cargados.
  const linkCode = searchParams.get("r");
  const [linkInfo, setLinkInfo] = useState(null);
  const [linkLoaded, setLinkLoaded] = useState(!linkCode);

  useEffect(() => {
    let active = true;
    api.get(`/public/clinics/${slug}`)
      .then(({ data }) => { if (active) setClinic(data); })
      .catch((err) => { if (active) setError(formatApiErrorDetail(err.response?.data?.detail)); });
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    if (!linkCode) return undefined;
    let active = true;
    api.get(`/public/clinics/${slug}/booking-link/${encodeURIComponent(linkCode)}`)
      .then(({ data }) => { if (active) setLinkInfo(data); })
      .catch(() => {})
      .finally(() => { if (active) setLinkLoaded(true); });
    return () => { active = false; };
  }, [slug, linkCode]);

  useEffect(() => {
    if (clinic?.name) document.title = `Reservar turno · ${clinic.name}`;
  }, [clinic]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F3ED] px-4" data-testid={PUBLIC_BOOKING.error}>
        <p className="text-[#5C554D] text-center max-w-sm">{error}</p>
      </div>
    );
  }

  if (!clinic || !linkLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F3ED]" data-testid={PUBLIC_BOOKING.loading}>
        <Loader2 className="w-8 h-8 animate-spin text-[#B99A62]" />
      </div>
    );
  }

  const linkLang = linkInfo && clinic.languages?.includes(linkInfo.language) ? linkInfo.language : null;
  const defaultLang = location.state?.lang || linkLang || (clinic.languages?.includes("es") ? "es" : "en");
  return (
    <PublicLangProvider defaultLang={defaultLang}>
      <PublicBookingContent
        clinic={clinic}
        preselectedServiceId={location.state?.preselectedServiceId || linkInfo?.service_id}
        linkCode={linkInfo ? linkCode : null}
        prefill={linkInfo ? { name: linkInfo.patient_name || "", phone: linkInfo.patient_phone || "" } : null}
      />
    </PublicLangProvider>
  );
}
